/**
 * Command Palette Component
 * 
 * Cmd+K quick access palette for instant navigation to any feature or action.
 * Provides fuzzy search across all application features, pages, and actions.
 * 
 * Features:
 * - Global keyboard shortcut (Cmd+K / Ctrl+K)
 * - Fuzzy search with highlighting
 * - Categorized results (Pages, Actions, Features)
 * - Recent items and favorites
 * - Keyboard navigation
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { Search, FileText, Settings, BarChart3, Users, Brain, Download, Upload, CheckCircle, Plus, Filter, Calendar, Database, Monitor, Key, HelpCircle } from 'lucide-react';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CommandItem {
  id: string;
  title: string;
  description: string;
  category: 'pages' | 'actions' | 'features' | 'shortcuts';
  icon: React.ReactNode;
  action: () => void;
  keywords: string[];
  badge?: string;
  badgeVariant?: 'default' | 'destructive' | 'secondary' | 'outline';
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, organization } = useAuth();
  const { toast } = useToast();
  const [search, setSearch] = useState('');

  // Command items with fuzzy search support
  const commandItems: CommandItem[] = useMemo(() => [
    // Pages
    {
      id: 'dashboard',
      title: 'Dashboard',
      description: 'Executive dashboard with KPIs and analytics',
      category: 'pages',
      icon: <BarChart3 className="h-4 w-4" />,
      action: () => navigate('/dashboard'),
      keywords: ['dashboard', 'executive', 'kpi', 'analytics', 'overview'],
    },
    {
      id: 'transactions',
      title: 'Transaktionen',
      description: 'Transaction review, upload, validation, and correction',
      category: 'pages',
      icon: <FileText className="h-4 w-4" />,
      action: () => navigate('/transactions'),
      keywords: ['transactions', 'transaktionen', 'buchung', 'booking', 'upload', 'validation'],
      badge: 'CONSOLIDATED',
      badgeVariant: 'secondary',
    },
    {
      id: 'learning-rules',
      title: 'Lernregeln',
      description: 'AI learning rules and pattern management',
      category: 'pages',
      icon: <Brain className="h-4 w-4" />,
      action: () => navigate('/learning-rules'),
      keywords: ['learning', 'lernregeln', 'ai', 'rules', 'patterns', 'machine learning'],
    },
    {
      id: 'reports',
      title: 'Berichte',
      description: 'Financial reports, exports, and period closing',
      category: 'pages',
      icon: <BarChart3 className="h-4 w-4" />,
      action: () => navigate('/reports'),
      keywords: ['reports', 'berichte', 'export', 'financial', 'period', 'closing'],
      badge: 'CONSOLIDATED',
      badgeVariant: 'secondary',
    },
    {
      id: 'settings',
      title: 'Einstellungen',
      description: 'User settings, team management, and configuration',
      category: 'pages',
      icon: <Settings className="h-4 w-4" />,
      action: () => navigate('/settings'),
      keywords: ['settings', 'einstellungen', 'profile', 'team', 'configuration'],
      badge: 'CONSOLIDATED',
      badgeVariant: 'secondary',
    },

    // Actions
    {
      id: 'book-transaction',
      title: 'Transaktion buchen',
      description: 'Transaktion in Hauptbuch buchen',
      category: 'actions',
      icon: <CheckCircle className="h-4 w-4" />,
      action: () => {
        navigate('/transactions');
        toast({
          title: 'Transaktion buchen',
          description: 'Navigiert zu Transaktionen für Buchung',
        });
      },
      keywords: ['book', 'buchen', 'transaction', 'transaktion', 'ledger', 'buchung'],
    },
    {
      id: 'upload-file',
      title: 'Datei hochladen',
      description: 'Bankauszüge oder CSV-Dateien hochladen',
      category: 'actions',
      icon: <Upload className="h-4 w-4" />,
      action: () => {
        navigate('/transactions?tab=upload');
        toast({
          title: 'Datei hochladen',
          description: 'Navigiert zu Upload-Tab',
        });
      },
      keywords: ['upload', 'hochladen', 'file', 'datei', 'csv', 'bank', 'statement'],
    },
    {
      id: 'create-report',
      title: 'Bericht erstellen',
      description: 'Create a new financial report',
      category: 'actions',
      icon: <Plus className="h-4 w-4" />,
      action: () => {
        navigate('/reports');
        toast({
          title: 'Bericht erstellen',
          description: 'Navigiert zu Berichte für neue Erstellung',
        });
      },
      keywords: ['create', 'erstellen', 'report', 'bericht', 'new', 'neu'],
    },
    {
      id: 'export-data',
      title: 'Daten exportieren',
      description: 'Daten zu Buchhaltungssystemen exportieren',
      category: 'actions',
      icon: <Download className="h-4 w-4" />,
      action: () => {
        navigate('/reports?tab=export');
        toast({
          title: 'Daten exportieren',
          description: 'Navigiert zu Export-Tab',
        });
      },
      keywords: ['export', 'exportieren', 'data', 'daten', 'accounting', 'buchhaltung'],
    },

    // Features
    {
      id: 'bulk-correction',
      title: 'Bulk-Korrektur',
      description: 'Correct multiple transactions at once',
      category: 'features',
      icon: <Filter className="h-4 w-4" />,
      action: () => {
        navigate('/transactions');
        toast({
          title: 'Bulk-Korrektur',
          description: 'Verwenden Sie die Bulk-Korrektur in Transaktionen',
        });
      },
      keywords: ['bulk', 'korrektur', 'multiple', 'batch', 'mass', 'edit'],
    },
    {
      id: 'learning-suggestions',
      title: 'KI-Vorschläge',
      description: 'View AI learning suggestions for transactions',
      category: 'features',
      icon: <Brain className="h-4 w-4" />,
      action: () => {
        navigate('/transactions');
        toast({
          title: 'KI-Vorschläge',
          description: 'KI-Vorschläge sind in der Transaktionsansicht verfügbar',
        });
      },
      keywords: ['ai', 'ki', 'suggestions', 'vorschläge', 'learning', 'machine'],
    },
    {
      id: 'validation-queue',
      title: 'Validierungswarteschlange',
      description: 'Review transactions requiring validation',
      category: 'features',
      icon: <CheckCircle className="h-4 w-4" />,
      action: () => {
        navigate('/transactions?tab=validation');
        toast({
          title: 'Validierungswarteschlange',
          description: 'Navigiert zu Validierungs-Tab',
        });
      },
      keywords: ['validation', 'validierung', 'queue', 'warteschlange', 'review', 'prüfung'],
    },

    // Shortcuts
    {
      id: 'keyboard-shortcuts',
      title: 'Tastenkürzel',
      description: 'View all available keyboard shortcuts',
      category: 'shortcuts',
      icon: <Key className="h-4 w-4" />,
      action: () => {
        navigate('/transactions');
        toast({
          title: 'Tastenkürzel',
          description: 'Drücken Sie Ctrl+? in Transaktionen für Tastenkürzel',
        });
      },
      keywords: ['shortcuts', 'tastenkürzel', 'keyboard', 'tastatur', 'help', 'hilfe'],
    },
    {
      id: 'help',
      title: 'Hilfe',
      description: 'Get help and support',
      category: 'shortcuts',
      icon: <HelpCircle className="h-4 w-4" />,
      action: () => {
        toast({
          title: 'Hilfe',
          description: 'Verwenden Sie Cmd+K für schnellen Zugriff auf alle Funktionen',
        });
      },
      keywords: ['help', 'hilfe', 'support', 'assistance', 'documentation'],
    },
  ], [navigate, toast]);

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!search) return commandItems;

    const searchLower = search.toLowerCase();
    return commandItems.filter(item => 
      item.title.toLowerCase().includes(searchLower) ||
      item.description.toLowerCase().includes(searchLower) ||
      item.keywords.some(keyword => keyword.toLowerCase().includes(searchLower))
    );
  }, [commandItems, search]);

  // Group items by category
  const groupedItems = useMemo(() => {
    const groups = {
      pages: filteredItems.filter(item => item.category === 'pages'),
      actions: filteredItems.filter(item => item.category === 'actions'),
      features: filteredItems.filter(item => item.category === 'features'),
      shortcuts: filteredItems.filter(item => item.category === 'shortcuts'),
    };

    return Object.entries(groups).filter(([_, items]) => items.length > 0);
  }, [filteredItems]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenChange(true);
      }
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onOpenChange, open]);

  const handleSelect = (item: CommandItem) => {
    item.action();
    onOpenChange(false);
    setSearch('');
  };

  const categoryLabels = {
    pages: 'Seiten',
    actions: 'Aktionen',
    features: 'Funktionen',
    shortcuts: 'Tastenkürzel',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Schnellzugriff
            <Badge variant="outline" className="ml-2">
              Cmd+K
            </Badge>
          </DialogTitle>
        </DialogHeader>
        
        <Command className="rounded-lg border-0">
          <CommandInput
            placeholder="Suchen Sie nach Seiten, Aktionen oder Funktionen..."
            value={search}
            onValueChange={setSearch}
            className="border-0 focus:ring-0"
          />
          <CommandList className="max-h-[400px]">
            <ScrollArea className="h-[400px]">
              {filteredItems.length === 0 ? (
                <CommandEmpty className="py-6 text-center">
                  <Search className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p>Keine Ergebnisse gefunden</p>
                  <p className="text-sm text-muted-foreground">
                    Versuchen Sie andere Suchbegriffe
                  </p>
                </CommandEmpty>
              ) : (
                groupedItems.map(([category, items]) => (
                  <React.Fragment key={category}>
                    <CommandGroup heading={categoryLabels[category as keyof typeof categoryLabels]}>
                      {items.map((item) => (
                        <CommandItem
                          key={item.id}
                          onSelect={() => handleSelect(item)}
                          className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                        >
                          <div className="flex-shrink-0 text-muted-foreground">
                            {item.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{item.title}</span>
                              {item.badge && (
                                <Badge variant={item.badgeVariant} className="text-xs">
                                  {item.badge}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {item.description}
                            </p>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    {groupedItems.indexOf([category, items]) < groupedItems.length - 1 && (
                      <CommandSeparator />
                    )}
                  </React.Fragment>
                ))
              )}
            </ScrollArea>
          </CommandList>
        </Command>

        <div className="px-6 py-3 border-t bg-muted/50">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <span>↑↓ Navigieren</span>
              <span>↵ Auswählen</span>
              <span>Esc Schließen</span>
            </div>
            <div className="flex items-center gap-2">
              <span>Powered by</span>
              <Badge variant="outline" className="text-xs">
                Swiss Finance OS
              </Badge>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
